import React from 'react';

import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FiMoreHorizontal, FiSmile } from 'react-icons/fi';
import { MdTranslate } from 'react-icons/md';
import { RiShareForwardLine } from 'react-icons/ri';

import { ChatMessageAttachment, ChatMessageStatus, ChatMessage as ChatMessageType } from '../../types';
import ChatAvatar from './ChatAvatar';
import { ChatBubble, ChatBubbleStatus, ChatFileTile, ChatMediaGrid, ChatQuote, ChatReactions } from './kit';

export const messageTime = (message: ChatMessageType) => (message.createdAt?.seconds ?? 0) * 1000;

// Optimistic messages have no id until the server has them.
const statusOf = (message: ChatMessageType): ChatBubbleStatus =>
  !message.id ? 'sending' : message.status === ChatMessageStatus.READ ? 'read' : 'sent';

export const replyPreview = (message?: Partial<ChatMessageType> | null) => {
  if (message?.text) {
    return message.text;
  }
  if (message?.images?.length) {
    return 'Photo';
  }
  if (message?.documents?.length) {
    return 'File';
  }
  return 'Message';
};

export const imageSrc = (image: ChatMessageAttachment) =>
  image.base64Data ? `data:image/png;base64,${image.base64Data}` : image.url || '';

interface Props {
  message: ChatMessageType;
  userId: string;
  isOwn: boolean;
  first: boolean;
  last: boolean;
  // Group chats show who wrote each burst.
  showSender: boolean;
  // Shown in place of the original text while set.
  translatedText?: string;
  translating?: boolean;
  onShowOriginal?: (message: ChatMessageType) => void;
  onJumpTo: (messageId: string) => void;
  onOpenMenu: (message: ChatMessageType, x: number, y: number, pickEmoji?: boolean) => void;
  onReaction: (message: ChatMessageType, emoji: string) => void;
  onReactionDetails: (message: ChatMessageType) => void;
  onReply: (message: ChatMessageType) => void;
  onOpenImage: (message: ChatMessageType, index: number) => void;
}

// A person-to-person message on the shared chat bubble. Memoised: a new
// message or a reaction re-renders only the bubbles it touches.
export const ChatMessage = React.memo<Props>(
  ({
    message,
    userId,
    isOwn,
    first,
    last,
    showSender,
    translatedText,
    translating,
    onShowOriginal,
    onJumpTo,
    onOpenMenu,
    onReaction,
    onReactionDetails,
    onReply,
    onOpenImage,
  }) => {
    const classes = useStyles();
    const side = isOwn ? 'out' : 'in';
    const replyToId = message.replyTo?.messageId;

    const openMenuAt = (event: React.MouseEvent, pickEmoji?: boolean) => {
      event.preventDefault();
      if (!message.id) {
        return;
      }
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      // A click on a button opens under it; a right click opens at the pointer.
      const fromButton = event.type === 'click';
      onOpenMenu(
        message,
        fromButton ? rect.left : event.clientX,
        fromButton ? rect.bottom : event.clientY,
        pickEmoji
      );
    };

    const reactions = (message.reactions || []).map(reaction => ({
      emoji: reaction.emoji,
      count: reaction.count,
      mine: reaction.hasReacted ?? !!reaction.userIds?.includes(userId),
    }));

    return (
      <ChatBubble
        side={side}
        first={first}
        last={last}
        time={messageTime(message)}
        status={isOwn ? statusOf(message) : undefined}
        edited={message.edited}
        senderName={showSender ? message.senderName : undefined}
        avatar={
          showSender ? (
            <ChatAvatar
              name={message.senderName}
              avatarUrl={message.senderAvatar}
              size={32}
              borderWidth={0}
            />
          ) : undefined
        }
        onContextMenu={openMenuAt}
        // Double click replies, as in Telegram.
        onDoubleClick={message.id ? () => onReply(message) : undefined}
        header={
          <>
            {message.forwardedFrom && (
              <Typography component="span" className={classes.forwarded}>
                <RiShareForwardLine size={14} /> Forwarded from {message.forwardedFrom.chatName}
              </Typography>
            )}
            {replyToId && (
              <ChatQuote
                side={side}
                title={
                  message.replyTo?.senderId === userId ? 'You' : message.replyTo?.senderName || 'Message'
                }
                text={replyPreview(message.replyTo)}
                onClick={() => onJumpTo(replyToId)}
              />
            )}
          </>
        }
        media={
          message.images?.length || message.documents?.length ? (
            <>
              {!!message.images?.length && (
                <ChatMediaGrid
                  images={message.images.map((image, index) => ({
                    key: `${index}-${image.name}`,
                    src: imageSrc(image),
                  }))}
                  onOpen={index => onOpenImage(message, index)}
                />
              )}
              {message.documents?.map((file, index) => (
                <ChatFileTile
                  key={`${index}-${file.name}`}
                  side={side}
                  name={file.name}
                  size={file.size}
                  onClick={
                    file.url ? () => window.open(file.url, '_blank', 'noopener,noreferrer') : undefined
                  }
                />
              ))}
            </>
          ) : undefined
        }
        note={
          translating || translatedText !== undefined ? (
            <Box className={classes.translation}>
              {translating ? <CircularProgress size={12} /> : <MdTranslate size={14} />}
              <span>{translating ? 'Translating…' : 'Translated'}</span>
              {!translating && onShowOriginal && (
                <span
                  role="button"
                  tabIndex={0}
                  className={classes.original}
                  onClick={() => onShowOriginal(message)}
                >
                  Show original
                </span>
              )}
            </Box>
          ) : undefined
        }
        actions={
          message.id ? (
            <>
              <IconButton
                size="small"
                className={classes.action}
                onClick={event => openMenuAt(event, true)}
                aria-label="React"
              >
                <FiSmile size={16} />
              </IconButton>
              <IconButton
                size="small"
                className={classes.action}
                onClick={event => openMenuAt(event)}
                aria-label="Message actions"
              >
                <FiMoreHorizontal size={16} />
              </IconButton>
            </>
          ) : undefined
        }
        below={
          reactions.length ? (
            <ChatReactions
              reactions={reactions}
              onToggle={emoji => onReaction(message, emoji)}
              onDetails={() => onReactionDetails(message)}
            />
          ) : undefined
        }
      >
        {message.text ? <span className={classes.text}>{translatedText ?? message.text}</span> : undefined}
      </ChatBubble>
    );
  }
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    text: { whiteSpace: 'pre-wrap' },
    forwarded: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 13,
      fontWeight: 500,
      lineHeight: '18px',
      marginBottom: 2,
      opacity: 0.85,
    },
    translation: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 4,
      fontSize: 12,
      opacity: 0.8,
    },
    original: { fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' },
    action: { color: theme.palette.text.secondary, backgroundColor: theme.palette.background.paper },
  })
);
